'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn.ts';

/* ── Dialog ──────────────────────────────────────────────────────────────── */

export type DialogSize = 'sm' | 'md' | 'lg';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Sits under the title, in the header. For a sentence that frames the whole dialog. */
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel: string;
  /**
   * How wide it opens. `sm` for a confirmation, `md` for a handful of fields, `lg` for a form
   * that would otherwise have to be scrolled to be read.
   */
  size?: DialogSize;
  className?: string;
}

/**
 * A modal built on the native `<dialog>` element.
 *
 * Using the platform rather than a div means focus trapping, the top layer, inertness of the
 * page behind, and Escape-to-close all come for free and correctly. Every hand-rolled modal
 * eventually gets one of those wrong — usually focus, and usually only for keyboard users, who
 * are the ones who needed it.
 *
 * The header and footer are fixed and only the body scrolls, so the title and the save button
 * stay where they were put no matter how long the form is.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeLabel,
  size = 'md',
  className,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) {
      return;
    }

    // showModal(), not the open attribute: only the former puts the dialog in the top layer and
    // makes the rest of the page inert.
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) {
      return;
    }

    // Escape closes the dialog natively; this keeps React's state in step with that, otherwise
    // the parent still thinks it is open and refuses to reopen it.
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);

    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  /** Clicking the backdrop closes it — the click lands on the dialog element itself. */
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      if (event.target === ref.current) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <dialog
      ref={ref}
      className={cn('awj-dialog', `awj-dialog--${size}`, className)}
      onClick={handleBackdropClick}
    >
      <div className="awj-dialog__header">
        <div className="awj-dialog__heading">
          <h2 className="awj-dialog__title">{title}</h2>
          {description ? <p className="awj-dialog__description">{description}</p> : null}
        </div>
        <button
          type="button"
          className="awj-dialog__close"
          onClick={onClose}
          aria-label={closeLabel}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="awj-dialog__body">{children}</div>

      {footer ? <div className="awj-dialog__footer">{footer}</div> : null}
    </dialog>
  );
}

/* ── Toasts ──────────────────────────────────────────────────────────────── */

export type ToastTone = 'default' | 'success' | 'danger';

export interface Toast {
  id: string;
  title: string;
  message?: string;
  tone?: ToastTone;
  /** Milliseconds. Zero keeps it until dismissed. */
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  show: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used inside a <ToastProvider>.');
  }

  return context;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Announced as the region's name. Localised by the app. */
  regionLabel: string;
  dismissLabel: string;
}

export function ToastProvider({ children, regionLabel, dismissLabel }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));

    const timer = timers.current.get(id);

    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Math.random().toString(36).slice(2)}`;
      const duration = toast.duration ?? 5000;

      setToasts((current) => [...current, { ...toast, id }]);

      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
    },
    [dismiss],
  );

  // Clear pending timers on unmount, so a dismissal cannot fire against a gone component.
  useEffect(() => {
    const pending = timers.current;

    return () => {
      for (const timer of pending.values()) {
        clearTimeout(timer);
      }

      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ toasts, show, dismiss }), [toasts, show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion
        toasts={toasts}
        onDismiss={dismiss}
        regionLabel={regionLabel}
        dismissLabel={dismissLabel}
      />
    </ToastContext.Provider>
  );
}

function ToastRegion({
  toasts,
  onDismiss,
  regionLabel,
  dismissLabel,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
  regionLabel: string;
  dismissLabel: string;
}) {
  return (
    // Polite rather than assertive: a toast is worth hearing at the next pause, not worth
    // interrupting whatever the reader is in the middle of.
    <div
      className="awj-toast-region"
      role="region"
      aria-label={regionLabel}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn('awj-toast', toast.tone && toast.tone !== 'default' && `awj-toast--${toast.tone}`)}
        >
          <span className="awj-toast__accent" aria-hidden="true" />
          <div className="awj-toast__content">
            <p className="awj-toast__title">{toast.title}</p>
            {toast.message ? <p className="awj-toast__message">{toast.message}</p> : null}
          </div>
          <button
            type="button"
            className="awj-dialog__close"
            onClick={() => onDismiss(toast.id)}
            aria-label={dismissLabel}
          >
            <CloseIcon />
          </button>
        </div>
      ))}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m4 4 8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
